<script setup lang="ts">
import { useI18n } from '@/i18n';
import AppCarousel from '@/components/AppCarousel/AppCarousel.vue';
import { LayoutGap, Prose } from '@dabble/quillui';

const { t } = useI18n();

const testimonials = [
  {
    image: '/images/authors/Victorine E. Lieske.jpg',
    author: 'Victorine E. Lieske',
    authorOf: 'Accidentally Married',
    quote:
      "I love being able to write on my laptop while I'm at home, then switch to writing on my phone while I'm waiting for my dinner at a restaurant, or while traveling. I love how seamless it is. I love how I can keep my writing notes and characters right in Dabble. I'm seriously in love with it!",
  },
  {
    image: '/images/authors/Travis Starnes.jpg',
    author: 'Travis Starnes',
    authorOf: 'The Sword of Jupiter',
    quote:
      "I write on multiple devices every day, and it makes it so my novels are always available. I've also found the outlining features amazingly helpful.",
  },
  {
    image: '/images/authors/Tory Steel.jpg',
    author: 'Tory Steel',
    authorOf: 'Breaking Tradition',
    quote:
      "Dabble has helped me stay organized, motivated, and connected with others. I've used Dabble to complete my latest novel that's going to be published Memorial Day weekend.",
  },
  {
    image: '/images/authors/Jaime Jo Wright.jpg',
    author: 'Jaime Jo Wright',
    authorOf: 'The House on Foster Hill',
    quote:
      "I have written most of my novels on Dabble and it is fabulous to always have access to my books wherever I go. I'll use the Dabble app to write on my phone on the fly and the user-friendly elements to the software make it less time consuming to learn unlike other writing software.",
  },
  {
    image: '/images/authors/Sally Britton.jpg',
    author: 'Sally Britton',
    authorOf: `Ingelwood Romances`,
    quote:
      'Dabble is an essential part of my writing career. It keeps me organized, focused, and motivated to get my word count in and my novels finished. I love it, and no other writing tool compares to it.',
  },
];
</script>

<template>
  <div class="auth-testimonial-carousel">
    <AppCarousel
      :items="testimonials"
      :interval="5000"
      :show-controls="false"
      :show-indicators="true"
      :auto-play="true"
    >
      <template #default="{ item }">
        <div class="auth-testimonial-carousel__slide">
          <p class="auth-testimonial-carousel__quote">{{ item.quote }}</p>
          <LayoutGap class="auth-testimonial-carousel__author" align="center" vertical-align="center">
            <img class="auth-testimonial-carousel__avatar" :src="item.image" :alt="item.author" :title="item.author" />
            <LayoutGap direction="column" align="left" vertical-align="center" size="tiny" :fill="false">
              <Prose weight="semibold">{{ item.author }}</Prose>
              <Prose variant="secondary" size="xsmall">
                {{ t('author_of') }} <i>{{ item.authorOf }}</i>
              </Prose>
            </LayoutGap>
          </LayoutGap>
        </div>
      </template>
    </AppCarousel>
  </div>
</template>

<style lang="scss" scoped>
.auth-testimonial-carousel {
  position: relative;
  width: 100%;
  height: 27rem;
  background: theme('color-fg-app');
  border-radius: borderRadius('large');
  box-shadow: 0 4px 32px 0 rgba(0, 0, 0, 0.2);
  padding: space('medium');
  overflow: hidden;

  &__slide {
    box-sizing: border-box;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: space('large');
    // Larger bottom padding keeps the vertically-centred content clear of the
    // carousel indicators, which are pinned to the bottom of the card.
    padding: space('small') space('small') space('medium');
    text-align: center;
  }

  &__quote {
    margin: 0;
    font-family: fontFamily('secondary');
    font-style: italic;
    font-size: fontSize('medium');
    line-height: lineHeight('default');
    color: theme('color-text-primary');
    position: relative;
    z-index: 2;

    &:before,
    &:after {
      display: inline;
      content: '"';
    }
  }

  &__avatar {
    width: size('huge');
    height: size('huge');
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid theme('color-border-primary-interactive');
  }
}
</style>
